import { usePostHerinnering } from "@/entities/herinnering/hooks.ts";
import {
    Button,
    Chip,
    DateTimeInput,
    ErrorDisplay,
    FormErrorMessage,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
    TimeInput,
} from "@/shared/components";
import { extractTimeAndDate, Time, timeTodayOrTomorrow } from "@/shared/utils";
import { PopoverPortal } from "@radix-ui/react-popover";
import { addMinutes, isValid } from "date-fns";
import { ReactNode, useMemo, useState } from "react";

type NewHerinneringProps = {
    dossierId: string;
};
export function NewHerinnering({ dossierId }: NewHerinneringProps) {
    const [herinneringText, setHerinneringText] = useState("");
    const putHerinnering = usePostHerinnering();

    const postReminder = (tekst: string, scheduledAt: Date) => {
        putHerinnering.mutate({ dossierId, tekst, scheduledAt }, { onSuccess: () => setHerinneringText("") });
    };

    const postDelayedReminder = (minutes: number) => () => {
        const scheduledFor = addMinutes(Date.now(), minutes);
        postReminder(herinneringText, scheduledFor);
    };

    return (
        <>
            <div className="flex flex-col items-start">
                <Input
                    name="herinnering"
                    placeholder="Herinnering toevoegen"
                    value={herinneringText}
                    onChange={(e) => setHerinneringText(e.target.value)}
                />
            </div>
            {herinneringText.length > 0 && (
                <div className="col-start-3 grid grid-cols-[0.8fr_0.8fr_0.8fr_1fr] gap-2">
                    <Chip label="10 min" checked={false} onChange={postDelayedReminder(10)} className="h-8 text-nowrap" />
                    <Chip label="20 min" checked={false} onChange={postDelayedReminder(20)} className="h-8 text-nowrap" />
                    <Chip label="30 min" checked={false} onChange={postDelayedReminder(30)} className="h-8 text-nowrap" />
                    <SetTimePopover onConfirm={(date) => postReminder(herinneringText, date)}>
                        <Button variant="secondary" size="s" className="shrink-0 text-nowrap">
                            Kies tijd
                        </Button>
                    </SetTimePopover>
                </div>
            )}
            {putHerinnering.error && (
                <div className="bg-default col-span-4 grid">
                    <ErrorDisplay error={putHerinnering.error} />
                </div>
            )}
        </>
    );
}

type CustomTimePopoverProps = {
    onConfirm: (date: Date) => void;
    children: ReactNode;
};

function SetTimePopover({ onConfirm, children }: CustomTimePopoverProps) {
    const [timeValue] = useMemo(() => extractTimeAndDate(addMinutes(new Date(), 40)), []);
    const [open, setOpen] = useState<boolean>(false);
    const [time, setTime] = useState<string | undefined>(timeValue);

    const scheduledFor = timeTodayOrTomorrow(time as Time);
    const isValidTime = isValid(scheduledFor);

    return (
        <Popover modal open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{children}</PopoverTrigger>

            <PopoverPortal>
                <PopoverContent side="right" align="center" sideOffset={20} className="px-3 py-2">
                    <div className="grid gap-2">
                        <div className="flex flex-row items-center justify-between gap-2">
                            <h4 className="text-l leading-m">Kies een tijd</h4>
                            <Button iconOnly icon="close" variant="tertiary" onClick={() => setOpen(false)} />
                        </div>
                        <DateTimeInput className="flex w-full">
                            <TimeInput value={time} onChange={(e) => setTime(e.target.value)} />
                        </DateTimeInput>
                        <Button variant="primary" disabled={!isValidTime} onClick={() => onConfirm(scheduledFor)}>
                            Herinnering toevoegen
                        </Button>
                        {!isValidTime && <FormErrorMessage>Vul een tijd in</FormErrorMessage>}
                    </div>
                </PopoverContent>
            </PopoverPortal>
        </Popover>
    );
}
